import React from 'react';

import P from "components/P";
import FlexChild from "layouts/flex/FlexChild";
import HorizontalFlex from "layouts/flex/HorizontalFlex";
import { useTouchClick } from "shared/hooks/useTouchClick";
import styles from "./Pagination.module.css"

function HorizontalPagination ({
    pagingData,
    onPageChange,
    leftIcon,
    rightIcon,
    style = {},
    maxVisibleBullets = 5,
    bottom
}) {

    const { currentPage, totalPages, hasNext, hasPrevious } = pagingData;
    const getVisiblePages = () => {
        const delta = Math.floor(maxVisibleBullets / 2);
        let start = currentPage - delta;
        let end = currentPage + delta;

        if (start < 0) {
            end += Math.abs(start);
            start = 0;
        }

        if (end >= totalPages) {
            start -= (end - totalPages + 1);
            end = totalPages - 1;
        }

        start = Math.max(start, 0);
        end = Math.min(end, totalPages - 1);

        const pages = [];

        // 첫 페이지
        if (start > 0) {
            pages.push(0);
        }

        // 중간 페이지들
        for (let i = start; i <= end; i++) {
            pages.push(i);
        }

        // 마지막 페이지
        if (end < totalPages - 1) {
            pages.push(totalPages - 1);
        }

        return pages;
    };

    // Calculate dynamic styles for each bullet
    const getBulletStyle = (pageIndex) => {
        const distance = Math.abs(currentPage - pageIndex);
        const maxDistance = Math.floor(maxVisibleBullets / 2);
        const scale = Math.max(0.6, 1 - (distance * 0.15)); // Scale from 1 to 0.6
        const opacity = Math.max(0.3, 1 - (distance * 0.25)); // Opacity from 1 to 0.3

        return {
            width: currentPage === pageIndex ? '24px' : `${12 * scale}px`,
            height: currentPage === pageIndex ? '24px' : `${12 * scale}px`,
            borderRadius: '50%',
            backgroundColor: currentPage === pageIndex ? 'var(--main-color)' : '#a0a0a0',
            border: currentPage === pageIndex ? "none" : "1px solid #a0a0a0",
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            opacity,
            transform: `scale(${scale})`,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            color: 'white',
            fontWeight: 'bold',
            boxShadow: currentPage === pageIndex 
            ? '0 4px 6px rgba(255, 255, 255, 0.1)' // 현재 페이지의 페이지 불릿에 그림자 추가
            : 'none'
            
        };
    };

    const handlePrevClick = () => {
        if (hasPrevious) {
            onPageChange('prev');
        }
    };

    const handleNextClick = () => {
        if (hasNext) {
            onPageChange('next');
        }
    };

    const { handleTouchStart: prevTouchStart, handleTouchEnd: prevTouchEnd } =
        useTouchClick(handlePrevClick, 10);

    const { handleTouchStart: nextTouchStart, handleTouchEnd: nextTouchEnd } =
        useTouchClick(handleNextClick, 10);

    return (
        <div style={{
            display: totalPages === 1 ? "none" :  'flex',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'absolute',
            bottom: bottom,
            width: '93%',
            ...style
        }}>
            {/* Previous button remains unchanged */}
            <div
                className={styles.btn}
                onPointerDown={prevTouchStart}
                onPointerUp={prevTouchEnd}
                style={{
                    width: "400px",
                    height: "44px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundColor: !hasPrevious ? "white" : "rgba(255, 121, 25, 1)",
                    border: !hasPrevious ? "1.5px solid rgba(255, 121, 25, 1)" : "white",
                    color: "#fff",
                    cursor: !hasPrevious ? "default" : "pointer",
                    borderRadius: '5px',
                    padding: '26px 10px',
                    transition: "transform 0.1s ease-in-out",
                    zIndex: 19,
                }}
            >
                <HorizontalFlex>
                    <FlexChild width={"40%"}>
                        <img
                            src={leftIcon}
                            width={"10px"}
                            style={{
                                filter: hasPrevious && "invert(50%) brightness(250%)"
                            }}
                        />
                    </FlexChild>
                    <FlexChild>
                        <P
                            font={"Pretendard"}
                            weight={700}
                            size={24}
                            color={!hasPrevious ? "rgba(255, 121, 25, 1)" : "white"}
                        >
                            이전
                        </P>
                    </FlexChild>
                </HorizontalFlex>
            </div>

            {/* 페이지 번호들 */}
            <div style={{
                display: "flex",
                flexDirection: "row",
                gap: "10px",
                justifyContent: "center",
                margin: "0 20px",
                height: "44px",
                alignItems: "center"
            }}>
                {getVisiblePages().map((pageIndex, idx) => (
                    <div
                        key={pageIndex}
                        style={getBulletStyle(pageIndex)}
                    >
                        <P size={18}>{currentPage === pageIndex && (pageIndex + 1)}</P>
                    </div>
                ))}
            </div>

            {/* Next button remains unchanged */}
            <div
                className={styles.btn}
                onPointerDown={nextTouchStart}
                onPointerUp={nextTouchEnd}
                style={{
                    width: "400px",
                    height: "44px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundColor: !hasNext ? "white" : "rgba(255, 121, 25, 1)",
                    border: !hasNext ? "1.5px solid rgba(255, 121, 25, 1)" : "white",
                    color: "#fff",
                    cursor: !hasNext ? "default" : "pointer",
                    borderRadius: '5px',
                    padding: '26px 10px',
                    transition: "transform 0.1s ease-in-out",
                    zIndex: 19,
                }}
            >
                <HorizontalFlex>
                    <FlexChild justifyContent={"flex-end"}>
                        <P
                            font={"Pretendard"}
                            weight={700}
                            size={24}
                            color={!hasNext ? "rgba(255, 121, 25, 1)" : "white"}
                        >
                            다음
                        </P>
                    </FlexChild>
                    <FlexChild width={"40%"} justifyContent={"flex-end"}>
                        <img
                            src={rightIcon}
                            width={"10px"}
                            style={{
                                filter: hasNext && "invert(50%) brightness(250%)"
                            }}
                        />
                    </FlexChild>
                </HorizontalFlex>
            </div>
        </div>
    );
};

export default HorizontalPagination;